import React, { useEffect, useMemo, useState } from "react";
import { Link, Typography, Box } from "@mui/material";
import type { Selected } from "../Context";

type TrainPlateProps = {
  train: {
    lineId: string;
    routeId: string;
    active: boolean;
    lastUpdate: Date | null;
    occupancy: number | null;
  };
  selected: Selected | null;
  setSelected: (sel: Selected | null) => void;
};

function occupancyColour(pct: number | null): string {
  if (pct === null || pct === undefined) return 'rgba(255,255,255,0.1)';
  if (pct >= 70) return '#FD0000';
  if (pct >= 30) return '#FFB600';
  return '#6FAC5B';
}

export const TrainPlate = ({ train, selected, setSelected }: TrainPlateProps) => {
  const blinkTime = 30000;

  const baseColor = useMemo(() => (train.active ? "#ffff54" : "#ffffff"), [train.active]);

  const [color, setColor] = useState(baseColor);
  const [bold, setBold] = useState<"normal" | "bold">("normal");

  useEffect(() => {
    const eventTime = train.lastUpdate ?? new Date(0);
    const diff = Date.now() - eventTime.getTime();

    if (diff < blinkTime) {
      setColor("#FFA500");
      setBold("bold");
    } else {
      setColor(baseColor);
      setBold("normal");
    }

    const remaining = Math.max(0, blinkTime - diff);
    const t = window.setTimeout(() => {
      setColor(baseColor);
      setBold("normal");
    }, remaining);

    return () => window.clearTimeout(t);
  }, [train.lastUpdate, baseColor]);

  const isSelected =
    selected?.lineId === train.lineId && selected?.routeId === train.routeId;

  const barColour = occupancyColour(train.occupancy);
  const barWidth  = train.occupancy !== null ? `${Math.min(100, train.occupancy)}%` : '0%';

  return (
    <Box
      sx={{
        px: '4px', py: '6px',
        ...(isSelected ? { background: "#163244" } : {}),
      }}
    >
      <Typography
        textAlign="center"
        fontWeight={bold}
        sx={{ fontSize: "12px", color: "#ffffff" }}
        noWrap
      >
        <Link
          color={color}
          underline="none"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            setSelected({ lineId: train.lineId, routeId: train.routeId });
          }}
        >
          {train.routeId}
        </Link>
      </Typography>

      {/* Occupancy bar */}
      <Box sx={{ mt: '3px', height: '3px', width: '100%', backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: '2px', overflow: 'hidden' }}>
        <Box sx={{ height: '100%', width: barWidth, backgroundColor: barColour, borderRadius: '2px', transition: 'width 0.4s ease, background-color 0.4s ease' }} />
      </Box>
    </Box>
  );
};
